import { useState } from 'react'
import { HiArrowRight } from 'react-icons/hi'
import {
  useGetSimpleWorkspaceViewsQuery,
  ViewableObjectType,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Button } from '../ui/atoms/button/Button'
import { AddNewBlockButton } from './AddNewBlockButton'
import { DeleteWorkspaceButton } from './DeleteWorkspaceButton'
import { MoveWorkspaceToProjectDialog } from './MoveWorkspaceToProjectDialog'
import { WorkspaceDetailsProvider } from './WorkspaceDetailsProvider'

interface WorkspaceActionsProps {
  workspaceId?: string
}

/**
 * Renders edit actions for a workspace (dashboard) page in the title bar
 *
 * The delete button is only shown for an empty workspace,
 * whereas the add new block button is always shown.
 * Move is shown when this workspace is a block inside another workspace (has a parent).
 */
export function WorkspaceActions({ workspaceId }: WorkspaceActionsProps) {
  const { t } = useTypedTranslation()
  const [openMoveDialog, setOpenMoveDialog] = useState(false)
  const { data } = useGetSimpleWorkspaceViewsQuery({
    variables: { workspaceId: workspaceId || '' },
    skip: !workspaceId,
  })
  if (!workspaceId) {
    return null
  }
  const parentWorkspaceId = data?.workspace.linkedViews[0]?.parentWorkspace.id ?? null
  const canMoveWorkspace = !!parentWorkspaceId

  return (
    <WorkspaceDetailsProvider workspaceId={workspaceId}>
      {data?.workspace.views && data.workspace.views.length === 0 && (
        <DeleteWorkspaceButton workspaceId={workspaceId} />
      )}
      {canMoveWorkspace && (
        <Button
          onClick={() => setOpenMoveDialog(true)}
          startIcon={<HiArrowRight className="size-4" />}
          variant="secondary"
        >
          {t('workspace.move_to')}
        </Button>
      )}
      <AddNewBlockButton workspaceId={workspaceId} />
      {canMoveWorkspace && (
        <MoveWorkspaceToProjectDialog
          objectId={workspaceId}
          objectType={ViewableObjectType.WORKSPACE}
          oldParentWorkspaceId={parentWorkspaceId}
          onClose={() => setOpenMoveDialog(false)}
          open={openMoveDialog}
        />
      )}
    </WorkspaceDetailsProvider>
  )
}
